<template>
    <div>

        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-4 mb-2 mb-lg-0"><b-input-group>
                            <b-form-input v-model.trim="searchText" type="search" :placeholder="$t('label.search')"
                                @keyup.enter="applySearch" />
                            <b-input-group-append><b-button variant="info" @click="applySearch"><i
                                        class="mdi mdi-magnify"></i></b-button></b-input-group-append>
                        </b-input-group></div>
                    <div class="col-lg-8 d-flex justify-content-lg-end"><b-button variant="info" @click="openForm()">
                            <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.productCondition')]) }}
                        </b-button></div>
                </div>
                <div v-if="!status.list" class="d-flex justify-content-center p-5"><b-spinner variant="info" /></div>
                <b-alert v-else-if="listError" show variant="danger">
                    {{ $t('label.productConditionLoadError') }}
                    <b-button size="sm" variant="outline-danger" @click="loadProductConditions">{{ $t('label.retryPermissionDefinitions') }}</b-button>
                </b-alert>
                <template v-else>
                    <b-table :items="tableRows" :fields="fields" show-empty responsive>
                        <template #cell(isActive)="{ item }">
                            <b-form-checkbox :key="`${item.uuid}-${statusToggleVersion}`" :checked="item.isActive" switch
                                :disabled="!status.save || statusTogglePending" @change="confirmStatus(item, $event)">
                                {{ item.isActive ? $t('label.active') : $t('label.inactive') }}
                            </b-form-checkbox>
                        </template>
                        <template #cell(actions)="{ item }">
                            <a href="javascript:void(0);" class="text-info" @click.stop="openForm(item)"><i
                                    class="mdi mdi-pencil-outline font-size-18"></i></a>
                            <a href="javascript:void(0);" class="text-danger ml-2" @click.stop="confirmDelete(item)"><i
                                    class="mdi mdi-delete-outline font-size-18"></i></a>
                        </template>
                    </b-table>
                    <div v-if="pagination.total" class="d-flex justify-content-between align-items-center mt-3">
                        <small class="text-muted">{{ $tc('label.results', pagination.total, [pagination.total])
                        }}</small>
                        <b-pagination v-model="currentPage" :total-rows="pagination.total"
                            :per-page="searchCriteria.pageSize" class="mb-0" @change="pageNavigation" />
                    </div>
                </template>
            </div>
        </div>
        <b-modal id="productCondition-form" :title="$t(productCondition.uuid ? 'label.updateX' : 'label.createX', [$t('module.productCondition')])"
            size="lg" hide-footer hide-header-close centered @hidden="closeForm"><b-container fluid>
                <ProductConditionForm v-if="formVisible" :condition-u-u-i-d="productCondition.uuid" :search-criteria="searchCriteria"
                    @done="closeForm" />
            </b-container></b-modal>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import i18n from '@/i18n';
import { productConditionMethods } from '@/state/api';
import { PRODUCT_CONDITION_MODULE } from '@/state/api/productCondition.state';
import ProductConditionForm from '@/views/pages/admin/productCondition/form';

const defaultCriteria = () => ({ pageNumber: 1, pageSize: 15, searchArray: [] });

export default {
    name: 'ProductConditionListing', components: { ProductConditionForm },
    data() {
        return {
            statusToggleVersion: 0, statusTogglePending: false,
            searchText: '', currentPage: 1, formVisible: false, productCondition: { uuid: '' }, searchCriteria: defaultCriteria(),
            fields: [
                { key: 'name', label: i18n.t('label.name') },
                { key: 'isActive', label: i18n.t('label.status') },
                { key: 'createdAt', label: i18n.t('label.createdAt') },
                { key: 'actions', label: i18n.t('label.action') },
            ],
        };
    },
    computed: {
        ...mapState(PRODUCT_CONDITION_MODULE, ['status', 'listError']),
        tableRows() {
            return this.$store.state.api.productCondition.list.data || [];
        },
        pagination() {
            return this.$store.state.api.productCondition.list.pagination || {};
        },
    },
    created() { this.loadProductConditions(); },
    methods: {
        ...productConditionMethods,
        loadProductConditions() {
            return this.getAllProductCondition({ filter: this.searchCriteria });
        },
        applySearch() {
            this.currentPage = 1; this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                searchArray: this.searchText ? [this.searchText] : []
            }; this.loadProductConditions();
        },
        pageNavigation(pageNumber) {
            this.currentPage = pageNumber;
            this.searchCriteria = { ...this.searchCriteria, pageNumber };
            this.loadProductConditions();
        },
        openForm(productCondition = null) {
            this.productCondition = { uuid: productCondition?.uuid || '' }; this.formVisible = true;
            this.$nextTick(() => this.$bvModal.show('productCondition-form'));
        },
        closeForm() {
            this.$bvModal.hide('productCondition-form'); this.formVisible = false; this.productCondition = { uuid: '' };
        },
        async confirmStatus(productCondition, isActive) {
            if (this.statusTogglePending || !this.status.save) return;
            this.statusTogglePending = true;
            try {
                const action = this.$t(isActive ? 'label.activate' : 'label.deactivate');
                const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('label.confirmChangeStatusX', [action, this.$t('module.productCondition')]),
                    { title: this.$t('label.confirmStatusChange'), okTitle: this.$t('label.confirm'), okVariant: 'info', cancelTitle: this.$t('label.cancel'), centered: true });
                if (confirmed) await this.updateProductConditionStatus({ uuid: productCondition.uuid, isActive, filter: this.searchCriteria });
            } finally {
                // Restore the saved status after cancellation or a failed request.
                this.statusToggleVersion++;
                this.statusTogglePending = false;
            }
        },
        async confirmDelete(productCondition) {
            const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('label.confirmDeleteX', [this.$t('module.productCondition')]),
                { title: this.$t('label.delete'), okTitle: this.$t('label.delete'), okVariant: 'danger', cancelTitle: this.$t('label.cancel'), centered: true });
            if (confirmed) {
                const result = await this.deleteProductCondition({ uuid: productCondition.uuid, filter: this.searchCriteria });
                if (result?.success && this.currentPage > 1 && !this.tableRows.length) {
                    this.pageNavigation(this.currentPage - 1);
                }
            }
        },
    },
};
</script>
